import type { NoticeRun } from './notice-fidelity.models';

/** The notice editor works with this bounded model, never with persisted HTML. */
export type NoticeInlineSegment =
  | { kind: 'text'; text: string; bold: boolean; italic: boolean }
  | { kind: 'break'; bold: boolean; italic: boolean }
  | {
      kind: 'reference';
      referenceKind: 'field' | 'term';
      code: string;
      bold: boolean;
      italic: boolean;
      format?: string;
    };

export function noticeRunsToSegments(
  runs: readonly NoticeRun[],
): NoticeInlineSegment[] {
  return runs.flatMap((run): NoticeInlineSegment[] => {
    if (run.kind !== 'literal') {
      return [
        {
          kind: 'reference',
          referenceKind: run.kind,
          code: run.value,
          bold: run.bold,
          italic: run.italic,
          ...(run.format === undefined ? {} : { format: run.format }),
        },
      ];
    }
    return noticePlainTextSegments(run.value, run.bold, run.italic);
  });
}

/** Pasted markup and token-looking text remain literal text. */
export function noticePlainTextSegments(
  text: string,
  bold = false,
  italic = false,
): NoticeInlineSegment[] {
  const result: NoticeInlineSegment[] = [];
  text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .forEach((line, index) => {
      if (index > 0) result.push({ kind: 'break', bold, italic });
      if (line) result.push({ kind: 'text', text: line, bold, italic });
    });
  return result;
}

export function noticeSegmentsToRuns(
  segments: readonly NoticeInlineSegment[],
): NoticeRun[] {
  const runs: NoticeRun[] = [];
  for (const segment of segments) {
    if (segment.kind === 'reference') {
      runs.push({
        kind: segment.referenceKind,
        value: segment.code,
        bold: segment.bold,
        italic: segment.italic,
        ...(segment.format === undefined ? {} : { format: segment.format }),
      });
      continue;
    }
    const value = segment.kind === 'break' ? '\n' : segment.text;
    if (!value) continue;
    const previous = runs.at(-1);
    if (
      previous?.kind === 'literal' &&
      previous.bold === segment.bold &&
      previous.italic === segment.italic
    ) {
      previous.value += value;
    } else {
      runs.push({
        kind: 'literal',
        value,
        bold: segment.bold,
        italic: segment.italic,
      });
    }
  }
  return runs;
}

export function normalizeNoticeRuns(runs: readonly NoticeRun[]): NoticeRun[] {
  return noticeSegmentsToRuns(noticeRunsToSegments(runs));
}

/** Only fragments issued by this page can restore reference nodes on paste. */
export class NoticeInlineClipboard {
  private readonly entries = new Map<string, NoticeRun[]>();

  remember(key: string, runs: readonly NoticeRun[]): void {
    if (JSON.stringify(runs).length > 65536) return;
    this.entries.set(key, structuredClone([...runs]));
    while (this.entries.size > 16)
      this.entries.delete(this.entries.keys().next().value!);
  }

  read(key: string): NoticeRun[] | null {
    const runs = this.entries.get(key);
    return runs ? structuredClone(runs) : null;
  }

  clear(): void {
    this.entries.clear();
  }
}
